import React, { useState, useEffect } from 'react';
import { MapPin, Trophy, Award, Calendar } from 'lucide-react';
import { achievements } from '../data/mockData';

const Logros = () => {
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  // Simple map visualization using markers
  const MapMarker = ({ achievement, index }) => {
    // Simplified positioning for demo (you can integrate a real map library later)
    const positions = [
      { top: '35%', left: '25%' }, // Brasil
      { top: '30%', left: '52%' }, // Francia
      { top: '32%', left: '53%' }, // Turin
      { top: '18%', left: '78%' }, // Tokio
      { top: '30%', left: '51%' }, // París
      { top: '30%', left: '52%' }, // Francia (general)
      { top: '42%', left: '29%' }, // Ecuador
      { top: '40%', left: '45%' }  // Mundial
    ];

    const position = positions[index] || { top: '50%', left: '50%' };

    return (
      <div
        className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
        style={{ top: position.top, left: position.left }}
        onClick={() => setSelectedAchievement(achievement)}
      >
        <div className="relative">
          <div className="w-8 h-8 bg-muro-gold rounded-full shadow-gold-lg flex items-center justify-center animate-pulse group-hover:scale-125 transition-transform">
            <Trophy className="w-4 h-4 text-muro-black" />
          </div>
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <div className="bg-muro-dark border border-muro-gold rounded-lg px-3 py-2 whitespace-nowrap">
              <p className="text-muro-gold text-xs font-semibold">{achievement.year}</p>
              <p className="text-muro-text text-xs">{achievement.title}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-muro-text">
              Logros <span className="text-gold-gradient">Internacionales</span>
            </h1>
            <p className="text-xl text-muro-text-muted">
              El muro que conquistó el mundo. Desde Las Condes hasta los estadios más importantes de Europa y Asia.
            </p>
          </div>
        </div>
      </section>

      {/* Interactive Map */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-muro-text mb-4">
                Mapa de <span className="text-gold-gradient">Conquistas</span>
              </h2>
              <p className="text-muro-text-muted">
                Haz clic en cada trofeo para conocer el logro
              </p>
            </div>

            {/* Map Container */}
            <div className="relative bg-muro-gray-900 rounded-lg overflow-hidden border-2 border-muro-gray-800 shadow-2xl">
              {/* World Map Silhouette */}
              <div 
                className="w-full h-[500px] bg-muro-gray-800 relative"
                style={{
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 1000 500\'%3E%3Crect fill=\'%232a2a2a\' width=\'1000\' height=\'500\'/%3E%3Cpath fill=\'%23353535\' d=\'M0,100 Q250,80 500,100 T1000,100 L1000,400 Q750,420 500,400 T0,400 Z\'/%3E%3C/svg%3E")',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Achievement Markers */}
                {achievements.map((achievement, index) => (
                  <MapMarker 
                    key={achievement.id} 
                    achievement={achievement} 
                    index={index}
                  />
                ))}
              </div>

              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-muro-dark/90 backdrop-blur-sm rounded-lg p-4 border border-muro-gold/20">
                <div className="flex items-center space-x-4 text-xs">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-muro-gold rounded-full"></div>
                    <span className="text-muro-text-muted">Club</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-muro-blue rounded-full"></div>
                    <span className="text-muro-text-muted">Selección</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-muro-green rounded-full"></div>
                    <span className="text-muro-text-muted">Individual</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Selected Achievement Details */}
            {selectedAchievement && (
              <div className="mt-8 glass-morphism rounded-lg p-8 border-2 border-muro-gold animate-in fade-in-up">
                <div className="flex items-start space-x-6">
                  <div className="bg-muro-gold/10 p-4 rounded-lg">
                    <Trophy className="w-12 h-12 text-muro-gold" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-muro-gold font-bold text-2xl">
                        {selectedAchievement.year}
                      </span>
                      <span className="px-3 py-1 bg-muro-gold/20 rounded-full text-muro-gold text-xs font-semibold uppercase">
                        {selectedAchievement.type === 'club' ? 'Club' : selectedAchievement.type === 'national' ? 'Selección' : 'Individual'}
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold text-muro-text mb-3">
                      {selectedAchievement.title}
                    </h3>
                    <p className="text-muro-text-muted text-lg mb-4">
                      {selectedAchievement.description}
                    </p>
                    <div className="flex items-center space-x-2 text-muro-text-muted">
                      <MapPin className="w-4 h-4" />
                      <span>{selectedAchievement.location}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Achievement Grid */}
      <section className="py-20 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-muro-text mb-4">
                Todos los <span className="text-gold-gradient">Logros</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {achievements.map((achievement) => (
                <div
                  key={achievement.id}
                  className="bg-muro-gray-900 rounded-lg p-6 card-hover cursor-pointer"
                  onClick={() => setSelectedAchievement(achievement)}
                >
                  <div className="flex items-start space-x-4 mb-4">
                    <div className={`p-3 rounded-lg ${
                      achievement.type === 'club' ? 'bg-muro-gold/10' :
                      achievement.type === 'national' ? 'bg-muro-blue/10' :
                      'bg-muro-green/10'
                    }`}>
                      {achievement.type === 'club' ? (
                        <Trophy className={`w-6 h-6 ${
                          achievement.type === 'club' ? 'text-muro-gold' :
                          achievement.type === 'national' ? 'text-muro-blue' :
                          'text-muro-green'
                        }`} />
                      ) : achievement.type === 'national' ? (
                        <Award className="w-6 h-6 text-muro-blue" />
                      ) : (
                        <Award className="w-6 h-6 text-muro-green" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-muro-gold text-sm font-semibold mb-1">
                        {achievement.year}
                      </div>
                      <h3 className="text-lg font-bold text-muro-text mb-1">
                        {achievement.title}
                      </h3>
                    </div>
                  </div>
                  <p className="text-muro-text-muted text-sm mb-3">
                    {achievement.description}
                  </p>
                  <div className="flex items-center space-x-2 text-xs text-muro-text-dark">
                    <MapPin className="w-3 h-3" />
                    <span>{achievement.location}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-muro-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">1</div>
                <p className="text-muro-text-muted">Champions League</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">1</div>
                <p className="text-muro-text-muted">Copa Libertadores</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">5</div>
                <p className="text-muro-text-muted">Ligas Francesas</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">5</div>
                <p className="text-muro-text-muted">Títulos en Chile</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">1</div>
                <p className="text-muro-text-muted">Copa del Mundo</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">1</div>
                <p className="text-muro-text-muted">Participación en JJ.OO.</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">5</div>
                <p className="text-muro-text-muted">Nominaciones The Best</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">1</div>
                <p className="text-muro-text-muted">The Best FIFA (Ganadora)</p>
              </div>
              <div className="space-y-2">
                <div className="text-5xl font-bold text-gold-gradient">3</div>
                <p className="text-muro-text-muted">11 Ideal FIFA/FIFPro</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Logros;
